// mui-imports
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project-imports
import { isEmptyObject } from 'helpers/objectFunctions';
import { dispatch, useSelector } from 'store';
import { getOrganizationDetails, patchOrganizationData, postOrganizationData } from 'store/slices/memberDetails';
import SubCard from 'ui-component/cards/SubCard';
import AutoCompleteInput from 'ui-component/extended/autocomplete';
import { MODULE_ACCESS_ORGANIZATION_DETAILS } from 'views/pages/member-details/constant';
import AutoCompleteCitySearch from 'views/pages/member-details/organization-dashboard/autoCompleteCitySearch';
import useStyles from '../../styles';

// third-party imports
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';

const validationSchema = yup.object({
    destinations: yup.array().min(1, 'Destination is required'),
    product_sold: yup.array().min(1, 'Product Sold is required'),
    acquisition_channel: yup.array().min(1, 'Acquisition Channel is required')
});

const EditOrganizationDetails = ({ incorporationYearsData, organizationCode }) => {
    const classes = useStyles();
    const theme = useTheme();

    const memberDetailsData = useSelector((state) => state?.memberDetails);
    const { data = {}, utilities = {}, isPostCallForOrganization = false } = memberDetailsData;

    const moduleAccessData = useSelector((state) => state.moduleAccess);
    const { moduleAccessMap = {} } = moduleAccessData || {};

    const { orgDetails = {} } = data || {};

    const {
        productSoldList = {},
        acquisitionChannelList = {},
        internationalDestinationList = [],
        domesticDestinationList = []
    } = utilities || {};

    const {
        incorporation_year: incorporationYear = '',
        acquisition_channel: acquisitionChannel = [],
        city = {},
        employees_count: employeesCount = null,
        internationalDestination = [],
        domesticDestination = [],
        product_sold: productSold = []
    } = orgDetails || {};

    const domesticDestinationArr = domesticDestination.map((destination) => destination?.id);
    const internationalDestinationArr = internationalDestination.map((destination) => destination?.id);
    const acquisitionDestinationArr = acquisitionChannel.map((channel) => channel?.id);

    const handleSubmit = async (values) => {
        const newValues = { ...values };
        newValues.city = newValues.city?.id;
        newValues.product_sold = newValues.product_sold.map((item) => item?.id);
        delete newValues.international_destination;
        delete newValues.domestic_destination;
        if (isPostCallForOrganization) {
            await dispatch(postOrganizationData(organizationCode, newValues));
        } else {
            await dispatch(patchOrganizationData(organizationCode, newValues));
        }
        await dispatch(getOrganizationDetails(organizationCode));
    };

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema,
        initialValues: {
            incorporation_year: incorporationYear,
            employees_count: employeesCount,
            acquisition_channel: acquisitionDestinationArr,
            domestic_destination: domesticDestinationArr,
            international_destination: internationalDestinationArr,
            destinations: [...domesticDestinationArr, ...internationalDestinationArr],
            product_sold: productSold,
            city
        },
        onSubmit: (values) => {
            handleSubmit(values);
        }
    });

    const handleCheckboxChange = (e, id, type, field) => {
        const { checked } = e.target;

        if (checked) {
            formik.setFieldValue([type], [...formik.values[type], id]);
            if (field === 'destinations') {
                formik.setFieldValue([field], [...formik.values[field], id]);
            }
        } else {
            formik.setFieldValue([type], [...formik.values[type].filter((item) => item !== id)]);
            if (field === 'destinations') {
                formik.setFieldValue([field], [...formik.values[field].filter((item) => item !== id)]);
            }
        }
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <Grid container item xs={12} spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" sx={{ fontWeight: 500 }}>
                            Edit Organization Details
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <SubCard sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                            <Grid container item spacing={4}>
                                <Grid item xs={6}>
                                    <AutoCompleteInput
                                        label="Products Sold"
                                        id="products_sold"
                                        options={Object.values(productSoldList)}
                                        isMultiSelect
                                        value={formik.values.product_sold}
                                        error={Boolean(formik.touched.product_sold && formik.errors.product_sold)}
                                        helperText={formik.touched.product_sold && formik.errors.product_sold}
                                        onChange={(event, newValue) => {
                                            const tempFilterTypeObj = {};
                                            newValue.forEach((item) => {
                                                tempFilterTypeObj[item.id] = item;
                                            });
                                            formik.setFieldValue('product_sold', newValue);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="company-incorporation-year">Company Incorporation Year</InputLabel>
                                        <Select
                                            id="company-incorporation-year-dropdown"
                                            label="Company Incorporation Year"
                                            name="incorporation_year"
                                            onChange={formik.handleChange}
                                            value={formik.values.incorporation_year}
                                        >
                                            {incorporationYearsData.map((option) => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <AutoCompleteCitySearch formik={formik} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        id="employees_count"
                                        name="employees_count"
                                        label="Number of Employees"
                                        type="number"
                                        onChange={formik.handleChange}
                                        value={formik.values.employees_count}
                                    />
                                </Grid>
                                {!isEmptyObject(acquisitionChannelList) && (
                                    <Grid container item spacing={1}>
                                        <Grid item>
                                            <Typography variant="h5">Acquisition Channels</Typography>
                                        </Grid>
                                        <Grid container item columnSpacing={2}>
                                            {Object.values(acquisitionChannelList).map((channel) => (
                                                <Grid item>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox checked={formik.values.acquisition_channel.includes(channel?.id)} />
                                                        }
                                                        name="acquisition_channel"
                                                        label={channel?.name}
                                                        className={classes.checkboxLabel}
                                                        onChange={(e) => {
                                                            handleCheckboxChange(e, channel?.id, 'acquisition_channel');
                                                        }}
                                                    />
                                                </Grid>
                                            ))}
                                            {formik.touched.acquisition_channel && formik.errors.acquisition_channel ? (
                                                <Grid item xs={12}>
                                                    <FormHelperText sx={{ color: theme.palette.error.main }}>
                                                        {formik.touched.acquisition_channel && formik.errors.acquisition_channel}
                                                    </FormHelperText>
                                                </Grid>
                                            ) : null}
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid container item>
                                    {internationalDestinationList.length > 0 && (
                                        <Grid item xs={6}>
                                            <Grid container item spacing={1}>
                                                <Grid item>
                                                    <Typography variant="h5">International Destination</Typography>
                                                </Grid>
                                                <Grid container item>
                                                    {internationalDestinationList.map((destination) => (
                                                        <Grid item xs={12}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={formik.values?.international_destination.includes(
                                                                            destination?.id
                                                                        )}
                                                                    />
                                                                }
                                                                name="international_destination"
                                                                label={destination?.name}
                                                                className={classes.checkboxLabel}
                                                                onChange={(e) => {
                                                                    handleCheckboxChange(
                                                                        e,
                                                                        destination?.id,
                                                                        'international_destination',
                                                                        'destinations'
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {domesticDestinationList.length > 0 && (
                                        <Grid item xs={6}>
                                            <Grid container item spacing={1}>
                                                <Grid item>
                                                    <Typography variant="h5">Domestic Destination</Typography>
                                                </Grid>
                                                <Grid container item>
                                                    {domesticDestinationList.map((destination) => (
                                                        <Grid item xs={12}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={formik.values?.domestic_destination.includes(
                                                                            destination?.id
                                                                        )}
                                                                    />
                                                                }
                                                                name="domestic_destination"
                                                                label={destination?.name}
                                                                className={classes.checkboxLabel}
                                                                onChange={(e) => {
                                                                    handleCheckboxChange(
                                                                        e,
                                                                        destination?.id,
                                                                        'domestic_destination',
                                                                        'destinations'
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {formik.touched.destinations && formik.errors.destinations ? (
                                        <Grid item xs={12}>
                                            <FormHelperText sx={{ color: theme.palette.error.main }}>
                                                {formik.touched.destinations && formik.errors.destinations}
                                            </FormHelperText>
                                        </Grid>
                                    ) : null}
                                </Grid>
                                <Grid container item justifyContent="center">
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            sx={{ width: '180px' }}
                                            disabled={
                                                !moduleAccessMap[MODULE_ACCESS_ORGANIZATION_DETAILS]?.is_active ||
                                                moduleAccessMap[MODULE_ACCESS_ORGANIZATION_DETAILS]?.action.toLowerCase() === 'view'
                                            }
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </form>
        </>
    );
};

EditOrganizationDetails.propTypes = {
    incorporationYearsData: PropTypes.array,
    organizationCode: PropTypes.string
};

export default EditOrganizationDetails;
